import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { X, Clock, ChefHat, Loader2, Bookmark, ArrowRight } from 'lucide-react';
import axios from 'axios';
import { scoreGoalFit, FitTag } from '@/utils/useGoalFit';

const API = '/api';

/**
 * RecipeBottomSheet — slides up from the bottom with 3-5 DB recipes.
 * Used by ProactiveCoachCard, RecoveryWindowCard, and any action button
 * that previously redirected to the AI Coach with a prefill prompt.
 *
 * Props:
 *   isOpen      - boolean
 *   onClose     - function
 *   searchQuery - string (e.g. "protein snack", "high-protein breakfast")
 *   category    - string (e.g. "snack", "breakfast", "dinner")
 *   title       - string displayed at top of sheet (e.g. "Protein Snack Ideas")
 */
export const RecipeBottomSheet = ({ isOpen, onClose, searchQuery = '', category = '', title = 'Recipe Ideas' }) => {
  const navigate = useNavigate();
  const { authHeaders, user } = useAuth();
  const { theme } = useTheme();
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dailyTotals, setDailyTotals] = useState({ calories: 0, protein: 0, carbs: 0, fat: 0 });

  useEffect(() => {
    if (!isOpen) return;
    const fetchRecipes = async () => {
      setLoading(true);
      try {
        const [recRes, mealsRes] = await Promise.all([
          axios.get(`${API}/recipes/library/browse?${new URLSearchParams({ ...(searchQuery ? { search: searchQuery } : {}), ...(category ? { category } : {}), limit: '5', sort: 'highest_protein' })}`, authHeaders()),
          axios.get(`${API}/meals?date=${new Date().toISOString().split('T')[0]}`, authHeaders()).catch(() => ({ data: [] })),
        ]);
        setRecipes(recRes.data?.recipes || []);
        const consumed = (mealsRes.data || []).reduce((acc, m) => {
          const t = m.final_analysis?.totals || {};
          return { calories: acc.calories + (t.calories || 0), protein: acc.protein + (t.protein || 0), carbs: acc.carbs + (t.carbs || 0), fat: acc.fat + (t.fat || 0) };
        }, { calories: 0, protein: 0, carbs: 0, fat: 0 });
        setDailyTotals(consumed);
      } catch {
        setRecipes([]);
      }
      setLoading(false);
    };
    fetchRecipes();
  }, [isOpen, searchQuery, category]);

  if (!isOpen) return null;

  const isDark = theme === 'dark';
  const targets = {
    calories: user?.calories_target || 2000, protein: user?.protein_target || 150,
    carbs: user?.carbs_target || 250, fat: user?.fat_target || 65,
  };
  const remainingMacros = {
    calories: targets.calories - dailyTotals.calories, protein: targets.protein - dailyTotals.protein,
    carbs: targets.carbs - dailyTotals.carbs, fat: targets.fat - dailyTotals.fat,
  };
  const bg = isDark ? '#1A2230' : '#FDFBF7';
  const cardBg = isDark ? '#232F3E' : '#FFFFFF';
  const textPrimary = isDark ? '#F0EDE8' : '#1A1A18';
  const textSecondary = isDark ? '#A1A1AA' : '#6A6560';
  const border = isDark ? 'rgba(255,255,255,0.08)' : '#E8E3DB';
  const accent = '#C67A5C';

  return ReactDOM.createPortal(
    <>
      {/* Backdrop */}
      <div
        style={{ position: 'fixed', inset: 0, zIndex: 9998, background: 'rgba(0,0,0,0.5)' }}
        onClick={onClose}
        data-testid="recipe-sheet-backdrop"
      />

      {/* Sheet */}
      <div
        style={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 9999,
          maxHeight: '75vh',
          borderTopLeftRadius: 20,
          borderTopRightRadius: 20,
          background: bg,
          boxShadow: '0 -8px 40px rgba(0,0,0,0.15)',
          paddingBottom: 'env(safe-area-inset-bottom, 16px)',
          animation: 'slideUpSheet 0.3s ease-out',
        }}
        data-testid="recipe-bottom-sheet"
      >
        {/* Handle */}
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 rounded-full" style={{ background: isDark ? 'rgba(255,255,255,0.2)' : '#D1CCC4' }} />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-5 pb-3">
          <h3 className="text-base font-semibold" style={{ color: textPrimary, fontFamily: 'var(--font-sans)' }}>{title}</h3>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-black/5 dark:hover:bg-white/10 transition" data-testid="recipe-sheet-close">
            <X className="w-5 h-5" style={{ color: textSecondary }} />
          </button>
        </div>

        {/* Content */}
        <div className="px-5 pb-6 overflow-y-auto" style={{ maxHeight: 'calc(75vh - 80px)' }}>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin" style={{ color: accent }} />
              <span className="ml-2 text-sm" style={{ color: textSecondary }}>Finding recipes...</span>
            </div>
          ) : recipes.length === 0 ? (
            <div className="text-center py-10">
              <ChefHat className="w-10 h-10 mx-auto mb-3" style={{ color: textSecondary, opacity: 0.5 }} />
              <p className="text-sm" style={{ color: textSecondary }}>No matching recipes found. Try the AI Coach for custom suggestions.</p>
              <button
                onClick={() => { onClose(); navigate('/ai-coach'); }}
                className="mt-3 px-4 py-2 rounded-full text-xs font-medium text-white transition hover:brightness-110"
                style={{ background: accent }}
                data-testid="recipe-sheet-ask-coach"
              >
                Ask AI Coach
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {recipes.map((recipe) => {
                const imgSlug = recipe.image_slug;
                const imgUrl = recipe.photo_url || (imgSlug ? `/food-images/recipes/${imgSlug}.png` : null);
                const recipeFood = { calories: recipe.calories || 0, protein: recipe.protein || 0, carbs: recipe.carbs || 0, fat: recipe.fat || 0 };
                const fit = scoreGoalFit(remainingMacros, recipeFood, user?.experience_mode || 'progress', 1);

                return (
                  <button
                    key={recipe.recipe_id}
                    onClick={() => { onClose(); navigate(`/recipe/${recipe.recipe_id}`); }}
                    className="w-full flex items-center gap-3 rounded-2xl p-3 transition-all hover:scale-[1.01] active:scale-[0.98]"
                    style={{ background: cardBg, border: `1px solid ${border}` }}
                    data-testid={`recipe-sheet-item-${recipe.recipe_id}`}
                  >
                    {/* Thumbnail */}
                    <div
                      className="flex-shrink-0 rounded-xl overflow-hidden flex items-center justify-center"
                      style={{ width: 64, height: 64, background: isDark ? '#2A3744' : '#F5F0E8' }}
                    >
                      {imgUrl ? (
                        <img
                          src={imgUrl}
                          alt={recipe.title}
                          className="w-full h-full object-cover"
                          onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                        />
                      ) : null}
                      <div
                        className="items-center justify-center"
                        style={{ display: imgUrl ? 'none' : 'flex', width: 64, height: 64 }}
                      >
                        <ChefHat className="w-6 h-6" style={{ color: textSecondary, opacity: 0.4 }} />
                      </div>
                    </div>

                    {/* Details */}
                    <div className="flex-1 min-w-0 text-left">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium truncate" style={{ color: textPrimary }}>{recipe.title}</p>
                        <FitTag fitLevel={fit.fitLevel} fitScore={fit.fitScore} compact />
                      </div>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-xs" style={{ color: accent, fontWeight: 600 }}>{recipe.calories || 0} cal</span>
                        <span className="text-xs" style={{ color: textSecondary }}>{recipe.protein || 0}g protein</span>
                        {recipe.total_time && (
                          <span className="flex items-center gap-0.5 text-xs" style={{ color: textSecondary }}>
                            <Clock className="w-3 h-3" />
                            {recipe.total_time}m
                          </span>
                        )}
                      </div>
                    </div>

                    <ArrowRight className="w-4 h-4 flex-shrink-0" style={{ color: textSecondary, opacity: 0.5 }} />
                  </button>
                );
              })}

              {/* Browse more link */}
              <button
                onClick={() => { onClose(); navigate('/meal-prep'); }}
                className="w-full text-center py-3 text-xs font-medium transition hover:opacity-80"
                style={{ color: accent }}
                data-testid="recipe-sheet-browse-all"
              >
                Browse full recipe library &rarr;
              </button>
            </div>
          )}
        </div>
      </div>
    </>,
    document.body
  );
};
